import { CheckoutFormData } from "./types";
import minDate from "date-fns/min";
import format from "date-fns/format";
import parse from "date-fns/parse";
import differenceInMinutes from "date-fns/differenceInMinutes";
import addMinutes from "date-fns/addMinutes";
import {
  $selectedActivities,
  SelectedActivity,
} from "../../../store/selected/selectedActivities";
import {
  $selectedPackages,
  SelectedPackage,
} from "../../../store/selected/selectedPackages";
import { $selectedLocation } from "../../../store/selected/selectedLocations";
import {
  $selectedSupplements,
  SelectedSupplement,
} from "../../../store/selected/selectedSupplements";
import { ReservationReserveRequestDto } from "../../../store/api/reservation/reserve/dtos";
import { $selectedPersonTypes } from "../../../store/selected/selectedPersonTypes";
import {
  Activity,
  ActivityAvailability,
  Package,
  PackageAvailability,
  PersonType,
} from "../../../store/models";
import { isPresent } from "../../../utils";
import { PersonTypesEndpoint } from "../../../store/endpoints";

type Result = ReservationReserveRequestDto;

export const compileReservationData = async (
  fd: CheckoutFormData
): Promise<Result | null> => {
  const location = $selectedLocation.getState();
  const personTypes = $selectedPersonTypes.getState();
  const packages = $selectedPackages.getState();
  const activities = $selectedActivities.getState();
  const supplements = $selectedSupplements.getState();

  if (!location) return null;

  const allPersonTypes = await PersonTypesEndpoint.list.call({
    _query: { per_page: 99999, location_id: location.id },
  });
  const systemPersonType = allPersonTypes.data.find(({ isSystem }) => isSystem);

  if (!systemPersonType) return null;

  const allDates: Date[] = [];
  packages.forEach(({ availabilitySlot }) => {
    allDates.push(availabilitySlot.date);
  });
  activities.forEach(({ availabilitySlot }) => {
    allDates.push(availabilitySlot.date);
  });

  const firstDate = minDate(allDates);

  const compiledPackages: Result["packages"] = packages.map((package_) => {
    const {
      entity,
      availabilitySlot,
      personTypes,
      supplementsServing,
      capacityPackageInstancesQuantity,
    } = package_;

    return {
      package_id: entity.id,
      capacity: entity.capacity,
      name: entity.name,
      start_date: format(availabilitySlot.date, "dd-MM-yyyy HH:mm"),
      person_types: personTypes.map(({ id, quantity }) => ({
        person_type_id: id,
        quantity,
      })),
      instances: compilePackageInstances(
        availabilitySlot,
        personTypes,
        allPersonTypes.data
      ),
      supplements: compilePackageSupplements(
        entity,
        availabilitySlot,
        supplementsServing
      ),
      activities: availabilitySlot.reservationData.activities.map(
        ({ activity_id }) => ({ activity_id })
      ),
      prices: capacityPackageInstancesQuantity
        ? [
            {
              person_type_id: systemPersonType.id,
              quantity: capacityPackageInstancesQuantity,
            },
          ]
        : personTypes.map(({ id, quantity }) => ({
            person_type_id: id,
            quantity,
          })),
    };
  });

  const compiledActivities: Result["activities"] = activities.map(
    (activity) => {
      const { entity, personTypes, availabilitySlot } = activity;

      const peopleAmount = personTypes.reduce((acc, { quantity, id }) => {
        const personTypeRecord = allPersonTypes.data.find(
          (record) => record.id === id
        );
        // check if person type is allocatable at this activity
        if (personTypeRecord?.activityIdToAllocatableMap?.[entity.id])
          return acc + quantity;
        return acc;
      }, 0);

      return {
        activity_id: entity.id,
        activity_name: entity.name,
        person_types: personTypes.map(({ id, quantity }) => ({
          person_type_id: id,
          quantity,
        })),
        instances: compileActivityInstances(
          availabilitySlot,
          activity.entity,
          peopleAmount
        ),
      };
    }
  );

  const compiledSupplements: Result["supplements"] = supplements.map(
    (supplement) => ({
      supplement_id: supplement.entity.id,
      name: supplement.entity.name,
      quantity: supplement.quantity,
      serving_time: compileSupplementServingTime(
        supplement,
        activities,
        packages
      ),
    })
  );

  const contact: Result["contacts"][0] = {
    given_name: fd.name,
    family_name: fd.last_name,
    email: fd.email,
    phone: fd.phone,
    birthday: fd.birthday ? format(fd.birthday, "dd-MM-yyyy") : undefined,
    country_code: fd.country,
  };

  const address: Result["addresses"][0] = {
    given_name: fd.name,
    family_name: fd.last_name,
    country_code: fd.country,
    city: fd.city,
    state: fd.state,
    street: fd.street,
    postal_code: fd.zip,
  };

  return {
    is_status_email_used_too_often_confirmed:
      fd.is_status_email_used_too_often_confirmed,
    location_id: location.id,
    date: format(firstDate, "dd-MM-yyyy"),
    person_types: personTypes.map(({ id, quantity }) => ({
      person_type_id: id,
      quantity,
    })),
    participants: personTypes.map(({ id }) => id),
    packages: compiledPackages,
    activities: compiledActivities,
    supplements: compiledSupplements,
    contacts: [contact],
    addresses: [address],
    customer_person_types: [
      {
        location_id: location.id,
        person_type_id: systemPersonType.id,
      },
    ],
    name: fd.company_name,
    vat: fd.company_vat,
    type: fd.company_name ? 1 : 0,
  };
};

// PackageInstances
type PackageInstance = Result["packages"][0]["instances"][0];
const compilePackageInstances = (
  availabilitySlot: PackageAvailability,
  personTypes: SelectedPackage["personTypes"],
  allPersonTypes: PersonType[]
): PackageInstance[] => {
  const instances: PackageInstance[] = [];

  availabilitySlot.reservationData.activities.forEach((activity) => {
    const { activity_id, slots } = activity;

    let remainPeopleToAllocate: number = personTypes.reduce(
      (acc, { quantity, id }) => {
        const personTypeRecord = allPersonTypes.find(
          (record) => record.id === id
        );
        // check if person type is allocatable at this activity
        if (personTypeRecord?.activityIdToAllocatableMap?.[activity_id])
          return acc + quantity;
        return acc;
      },
      0
    );

    slots.forEach((slot) => {
      const {
        slot_id,
        capacity,
        instance_start_time,
        instance_end_time,
      } = slot;

      const startDate = parse(
        instance_start_time,
        "yyyy-MM-dd HH:mm:ss",
        new Date()
      );
      const endDate = parse(
        instance_end_time,
        "yyyy-MM-dd HH:mm:ss",
        new Date()
      );

      const instancePeopleAmount = Math.min(remainPeopleToAllocate, capacity);

      remainPeopleToAllocate -= instancePeopleAmount;

      instances.push({
        slot_id: slot_id,
        activity_id: activity_id,
        start_date: format(startDate, "dd-MM-yyyy HH:mm"),
        end_date: format(endDate, "dd-MM-yyyy HH:mm"),
        people_amount: instancePeopleAmount,
      });
    });
  });

  return instances;
};

// PackageSupplements
type PackageSupplement = Result["packages"][0]["supplements"][0];
const compilePackageSupplements = (
  entity: Package,
  availabilitySlot: PackageAvailability,
  supplementsServing: SelectedPackage["supplementsServing"]
): PackageSupplement[] => {
  const reformatDate = (str: string): string => {
    const date = parse(str, "yyyy-MM-dd HH:mm:ss", new Date());
    return format(date, "dd-MM-yyyy HH:mm");
  };

  let startForHiddenSupplements: string | null = null;

  const instanceStart =
    availabilitySlot.reservationData.activities[0]?.slots[0]
      ?.instance_start_time;
  if (instanceStart) {
    startForHiddenSupplements = reformatDate(instanceStart);
  }

  return entity.supplements.map(({ id, name }) => {
    const supplementServing = supplementsServing.find((serving) => {
      return serving.id === id;
    });

    const activity = availabilitySlot.reservationData.activities.find(
      ({ activity_id }) => activity_id === supplementServing?.servingActivityId
    );

    const dateRaw: string | null =
      activity?.slots[0]?.instance_start_time ?? null;

    const date: string | null = dateRaw
      ? reformatDate(dateRaw)
      : startForHiddenSupplements;

    if (!isPresent(date)) {
      throw new Error(
        `No date found for supplement ${name} #${id}. Query: activity_id: ${id}`
      );
    }

    return {
      supplement_id: id,
      serving_time: date,
    };
  });
};

// ActivityInstances
type ActivityInstance = Result["activities"][0]["instances"][0];
const compileActivityInstances = (
  availabilitySlot: ActivityAvailability,
  activity: Activity,
  peopleToAllocate: number
): ActivityInstance[] => {
  let remainPeopleToAllocate: number = peopleToAllocate;

  return availabilitySlot.reservationData.slots.map((slot) => {
    const { slot_id, capacity, instance_start_time, instance_end_time } = slot;

    const times = {
      start: parse(instance_start_time, "yyyy-MM-dd HH:mm:ss", new Date()),
      end: parse(instance_end_time, "yyyy-MM-dd HH:mm:ss", new Date()),
    };

    const instancePeopleAmount = Math.min(remainPeopleToAllocate, capacity);

    remainPeopleToAllocate -= instancePeopleAmount;

    return {
      slot_id: slot_id,
      people_amount: instancePeopleAmount,
      activity_id: activity.id,
      person_type_id: availabilitySlot.reservationData.price.person_type_id,
      start_date: format(times.start, "dd-MM-yyyy HH:mm"),
      end_date: format(times.end, "dd-MM-yyyy HH:mm"),
      time_slots: compileActivityInstanceTimeSlots(slot_id, activity, times),
    };
  });
};

// ActivityInstances
type ActivityInstanceTimeSlot = Result["activities"][0]["instances"][0]["time_slots"][0];
const compileActivityInstanceTimeSlots = (
  slotId: number,
  activity: Activity,
  times: { start: Date; end: Date }
): ActivityInstanceTimeSlot[] => {
  const diffInMinutes = differenceInMinutes(times.end, times.start);
  const timeSlots: ActivityInstanceTimeSlot[] = [];

  for (
    let minutesHandled = 0;
    diffInMinutes > minutesHandled;
    minutesHandled += activity.slot_duration
  ) {
    const startDate = addMinutes(times.start, minutesHandled);
    const endDate = addMinutes(
      times.start,
      minutesHandled + activity.slot_duration
    );

    timeSlots.push({
      slot_id: slotId,
      start_date: format(startDate, "dd-MM-yyyy HH:mm"),
      end_date: format(endDate, "dd-MM-yyyy HH:mm"),
    });
  }

  return timeSlots;
};

// SupplementServingTime
type SupplementServingTime = Result["supplements"][0]["serving_time"];
const compileSupplementServingTime = (
  supplement: SelectedSupplement,
  activities: SelectedActivity[],
  packages: SelectedPackage[]
): SupplementServingTime => {
  let servingTime: SupplementServingTime | null = null;

  if (supplement.serving.packageId) {
    const package_ = packages.find(
      ({ entity: { id } }) => id === supplement.serving.packageId
    );
    if (package_) {
      const activity = package_.availabilitySlot.reservationData.activities.find(
        ({ activity_id }) => activity_id === supplement.serving.activityId
      );
      if (activity) {
        const date = parse(
          activity.slots[0].instance_start_time,
          "yyyy-MM-dd HH:mm:ss",
          new Date()
        );
        servingTime = format(date, "dd-MM-yyyy HH:mm");
      }
    }
  } else {
    const activity = activities.find(
      ({ entity: { id } }) => id === supplement.serving.activityId
    );
    if (activity) {
      const date = parse(
        activity.availabilitySlot.reservationData.slots[0].instance_start_time,
        "yyyy-MM-dd HH:mm:ss",
        new Date()
      );
      servingTime = format(date, "dd-MM-yyyy HH:mm");
    }
  }

  if (!servingTime) throw new Error("No serving time found for supplement");

  return servingTime;
};
